import styled from '@emotion/styled';
import iphoneDeviceCutUrl from '@/assets/devices/iphone-device-cut.png?url';
import appleSettingsIconUrl from '@/assets/icons/apple-settings.svg?url';
import exclamationCircleFillBadgeUrl from '@/assets/sf-symbols/exclamationmark.circle.fill.badge.png?url';

const SETTINGS_SIZES = {
  sm: 26,
  notification: 38,
  md: 66,
  lg: 72,
} as const;

const PHONE_SIZES = {
  sm: 82,
  md: 96,
  lg: 104,
} as const;

type SettingsSize = keyof typeof SETTINGS_SIZES;
type PhoneSize = keyof typeof PHONE_SIZES;
type PhoneTone = 'dark' | 'red';

const SettingsRoot = styled.span<{ $size: number }>`
  --icon-size: ${({ $size }) => `${$size}px`};
  position: relative;
  display: inline-flex;
  width: var(--icon-size);
  height: var(--icon-size);
  line-height: 0;
`;

const SettingsImage = styled.img<{ $shadow: boolean; $trimTransparentGutter: boolean }>`
  display: block;
  width: ${({ $trimTransparentGutter }) => ($trimTransparentGutter ? '111.12%' : '100%')};
  height: ${({ $trimTransparentGutter }) => ($trimTransparentGutter ? '111.12%' : '100%')};
  margin: ${({ $trimTransparentGutter }) => ($trimTransparentGutter ? '-5.56%' : '0')};
  border-radius: calc(var(--icon-size) * 0.22);
  background: transparent;
  filter: ${({ $shadow }) => ($shadow ? 'drop-shadow(0 7px 15px rgba(0, 0, 0, 0.2))' : 'none')};
  flex: 0 0 auto;
`;

const PhoneImage = styled.img<{ $height: number }>`
  display: block;
  width: auto;
  height: ${({ $height }) => `${$height}px`};
  object-fit: contain;
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.2));
`;

interface IosSettingsIconProps {
  badge?: boolean;
  badgeLabel?: number | string;
  badgeShadow?: boolean;
  className?: string;
  shadow?: boolean;
  size?: SettingsSize;
  trimTransparentGutter?: boolean;
}

const Badge = styled.img<{ $shadow: boolean }>`
  position: absolute;
  top: -9%;
  right: -8%;
  width: 34%;
  height: 34%;
  display: block;
  filter: ${({ $shadow }) => ($shadow ? 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.16))' : 'none')};
`;

const CountBadge = styled.span`
  position: absolute;
  top: -7%;
  right: -10%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: calc(var(--icon-size) * 0.3);
  height: calc(var(--icon-size) * 0.3);
  padding: 0 calc(var(--icon-size) * 0.055);
  border-radius: 999px;
  background: #ff3b30;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif;
  font-size: calc(var(--icon-size) * 0.13);
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

export function IosSettingsIcon({
  badge = false,
  badgeLabel,
  badgeShadow = true,
  className,
  shadow = true,
  size = 'md',
  trimTransparentGutter = false,
}: IosSettingsIconProps) {
  return (
    <SettingsRoot className={className} $size={SETTINGS_SIZES[size]}>
      <SettingsImage
        src={appleSettingsIconUrl}
        aria-hidden="true"
        alt=""
        $shadow={shadow}
        $trimTransparentGutter={trimTransparentGutter}
      />
      {badge && (
        badgeLabel === undefined
          ? <Badge src={exclamationCircleFillBadgeUrl} aria-hidden="true" alt="" $shadow={badgeShadow} />
          : <CountBadge aria-hidden="true">{badgeLabel}</CountBadge>
      )}
    </SettingsRoot>
  );
}

interface MiniIphoneIconProps {
  className?: string;
  size?: PhoneSize;
  tone?: PhoneTone;
}

export function MiniIphoneIcon({
  className,
  size = 'md',
}: MiniIphoneIconProps) {
  return (
    <PhoneImage
      aria-hidden="true"
      alt=""
      className={className}
      decoding="async"
      draggable={false}
      $height={PHONE_SIZES[size]}
      src={iphoneDeviceCutUrl}
    />
  );
}
